import React, { useContext, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/css';
import { useRouter } from 'next/router';
import { eq, size, toString, partition } from 'lodash';

import classNames from 'classnames';
import { getLocalizedString } from 'config/Localization.bs';
import { Maybe } from 'models/types';
import { Card_t } from 'models/Models.gen';
import { EMPTY_ELEMENT } from 'common/utils/react';
import { EMPTY_STRING } from 'common/utils/string';
import { DrainContext, DrainValue, SortValue } from 'contexts/DrainContext';
import { FilterTaxonomy, getFilterTaxonomyOptions } from 'api/queries/filter';
import { SimilarResults } from 'components/library/CardSearchResults.bs';
import { t as StoreContent_t } from 'api/graphql/StoreContentContentType/Gql__StoreContent__Decoder.gen';
import { t as EnrichedTextBlock_t } from 'api/graphql/EnrichedTextBlock/Gql__EnrichedTextBlock__Decoder.gen';
import { t as SkuPresentation_t } from 'api/graphql/SkuPresentation/Gql__SkuPresentation__Fragment__Decoder.gen';
import { make as EnrichedTextBlock } from 'components/enrichedTextSection/EnrichedTextSection.bs';
import Card from 'components/library/Card';
import { make as BrazeContentCards } from 'components/library/BrazeContentCards.bs';
import {
  cardsContainer as WallOfCardsStyles_cardsContainer,
  description as WallOfCardsStyles_description,
  rootContainerWall as WallOfCardsStyles_rootContainerWall,
  similarResultsContainer as WallOfCardsStyles_similarResultsContainer,
  title as WallOfCardsStyles_title,
} from 'components/pages/store/WallOfCardsStyles.bs';
import NoSearchResults from 'components/library/NoSearchResults';
import { useLayoutData } from 'contexts/LayoutDataContext';

import { flowList } from 'utils/fp';
import { FilterSection } from './FilterSection';
import { DrainSection } from './DrainSection';
import SelectSorting from './sorting';
import { getEnrichedTextSection, getSmartCardHeroSection } from 'common/storeSmartCardContent';
import { EMPTY_ARRAY } from 'common/utils/array';
import { specialRegex } from 'utils/Regex';

export const removeHiddenCards = (cards: Card_t[]) => cards.filter((card: Card_t) => !card.isPcCategoryHidden);

function getCardLists({ search, cards }: { search: string; cards: Card_t[] }): {
  filteredCards: Card_t[];
  similarCards: Card_t[];
} {
  function format(str: string): string {
    return str.replace(/\s/gi, EMPTY_STRING).replace(new RegExp(specialRegex, 'g'), EMPTY_STRING);
  }
  const regExp = new RegExp(format(search), 'i');
  const [filteredCards, similarCards] = partition(cards, card => regExp.test(format(card.searchableTerms)));

  return {
    filteredCards,
    similarCards,
  };
}

function formatCardAmount(amount: number): string {
  return amount < 10 ? `0${amount}` : amount > 999 ? amount.toLocaleString('en-US') : toString(amount);
}

/**
 * Appends SKU Presentation dictionary to the Card
 * @param dict
 */
export function appendDict(dict: Array<[string, SkuPresentation_t]>) {
  return (cardList: Card_t[]): Card_t[] => {
    return cardList.map((card: Card_t) => {
      const cardDictIndex = dict.findIndex((value: [string, SkuPresentation_t]) => value[0] === card.cmsId);
      return {
        ...card,
        presentation: cardDictIndex !== -1 ? dict[cardDictIndex][1] : void 0,
      };
    });
  };
}

const classes = {
  root: css`
    display: flex;
    flex-direction: column;
  `,
  filter: css``,
  smartContainer: {
    root: css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    `,
    item: css`
      width: 100%;
      &:first-child {
        @media (max-width: 1279px) {
          width: 100% !important;
        }
        width: 70%;
      }
      &:last-child {
        @media (max-width: 1279px) {
          display: none;
        }
        display: flex;
        width: 200px;
        padding-right: 20px;
      }
    `,
  },
  sortSection: css`
    position: absolute;
    top: 330px;
    right: 20px;
  `,
  smartLabel: css`
    width: 70%;
  `,
  cards: css`
    padding-top: 30px;
    @media (max-width: 592px) {
      padding-top: 30px;
    }
  `,
};

// @todo: typing
type Props = {
  inventoryProps: Card_t[];
  storeContent: Maybe<{ VAL: StoreContent_t }>;
  skuDict: Maybe<Array<[string, SkuPresentation_t]>>; // @todo: SkuPresentation_t as Js.Dict.t<type> definition
  topContent?: Maybe<EnrichedTextBlock_t>;
  filterList: FilterTaxonomy;
};

const WallOfCards: React.FC<Props> = ({
  inventoryProps,
  storeContent,
  topContent,
  skuDict,
  filterList,
}): React.ReactElement => {
  const drain = useContext(DrainContext);

  const router = useRouter();
  const searchTerm: Maybe<string | string[]> = router.query?.search;
  const [firstLoad, setFirstLoad] = useState(() => true);

  const drainScrollRef = useRef<HTMLHeadingElement>(null);

  const [search, setSearch] = useState(() => {
    const searchTermTmp: string = typeof searchTerm === 'object' ? searchTerm[0] : searchTerm || EMPTY_STRING;
    return searchTermTmp
      .split(EMPTY_STRING)
      .map((value: string) => {
        const str = decodeURIComponent(value);
        return str.replace(/([^\w\s\d])/g, '$1');
      })
      .join(EMPTY_STRING);
  });

  const layoutData = useLayoutData();
  const noSearchResultsData = layoutData.noSearchResults
    ? layoutData.noSearchResults
    : {
        title: 'Oops, No Results Found.',
        description:
          'Your search has yielded no results. Try again with a different brand to find the best gift you are looking for.',
        icon: {
          src: '/images/no_search_icon.png',
          alt: 'magnifying glass icon',
          width: 72,
          height: 72,
        },
        scp_title: 'Why not gift the choice with a Smart eGift Card?',
        scp_description:
          "It's the ideal swap card that lets your recipient turn it into all the big brand retailer cards. Personalise it with your own photo and a video message. Perfect gift done!",
      };

  const { current: currentInventory }: DrainValue = flowList([
    drain.restore,
    drain.applySort,
    // drain.applySearch,
    // drain.applyFilter,
    drain.initValue,
    appendDict(skuDict),
    removeHiddenCards,
  ])(inventoryProps);

  const [cardLists, setCardLists] = useState(() =>
    getCardLists({
      search,
      cards: currentInventory,
    })
  );

  // @todo: is it necessary?
  const noSearchWords = search === EMPTY_STRING && currentInventory.length === cardLists.filteredCards.length;

  const allSwapCards = currentInventory.filter(({ isSwappable }) => isSwappable);
  const otherCards = currentInventory.filter(({ isSwappable }) => !isSwappable);

  const [filterPanelOpen, toggleFilterPanel] = React.useState(false);

  const closeFilterPanel = () => {
    toggleFilterPanel(false);
  };

  useEffect(() => {
    setFirstLoad(false);
  }, []);

  useEffect(() => {
    closeFilterPanel();

    const { current: currentInventory }: DrainValue = flowList([
      drain.applySearch,
      drain.restore,
      drain.applySort,
      drain.applyFilter,
      drain.initValue,
      appendDict(skuDict),
      removeHiddenCards,
    ])(inventoryProps);

    const lists = getCardLists({
      search: drain.search,
      cards: currentInventory,
    });
    setCardLists(lists);
  }, [drain.apply, drain.search]);

  const hasSwapCards = allSwapCards.length > 0;
  const hasCards = cardLists.filteredCards.length > 0;
  const shouldRemoveSmartCards = !drain.search && !drain.hasSort() && !drain.hasFilter();
  const displayedCards = shouldRemoveSmartCards
    ? cardLists.filteredCards.filter(({ isSwappable }) => !isSwappable)
    : cardLists.filteredCards;

  const defaultStoreContent: StoreContent_t = {
    smart_card_content: void 0,
    retailer_card_content: void 0,
    end_of_page_content: void 0,
  };
  const storeContentDef = storeContent?.VAL || defaultStoreContent;
  const retailerContent = storeContentDef.retailer_card_content ? (
    <EnrichedTextBlock block={storeContentDef.retailer_card_content} wallOfCards={true} />
  ) : (
    <h1 className="text-2xl sm:text-32px leading-8 font-semibold pt-4 mx-4 pb-2 sm:pb-3">
      See eGift Cards from top brands
    </h1>
  );
  const smartCardHeroSection = storeContentDef.smart_card_content
    ? getSmartCardHeroSection(storeContentDef.smart_card_content)
    : null;
  const defaultSmartCardStoreContent = (
    <>
      <h2 className="text-2xl sm:text-32px leading-8 font-semibold pt-4 mx-4 pb-2 sm:pb-3">
        Prezzee Smart eGift Cards
      </h2>
      <h3 className="text-base mx-4 text-gray-600">
        For the ultimate shopping spree wrapped up in one great eGift Card, send a Prezzee Smart eGift Card. It&apos;s{' '}
        the ideal swap card that lets your recipient turn it into all the big brand retailer cards.{' '}
        {getLocalizedString('Personalise')} it with your own photo and a video message. Perfect gift done!
      </h3>
    </>
  );
  const smartCardStoreContent = storeContentDef.smart_card_content ? (
    <>{getEnrichedTextSection(storeContentDef.smart_card_content) || defaultSmartCardStoreContent}</>
  ) : (
    defaultSmartCardStoreContent
  );
  const endOfPageStoreContent = storeContentDef.end_of_page_content ? (
    <EnrichedTextBlock block={storeContentDef.end_of_page_content} wallOfCards={true} />
  ) : (
    void 0
  );

  return (
    <>
      <DrainSection open={drain.isOpen} filterList={filterList} scrollRef={drainScrollRef} />
      <div
        className={classNames({
          'overflow-hidden bg-white': filterPanelOpen,
          'relative bg-white': true,
        })}
      >
        <div className="flex flex-nowrap w-full text-gray-600">
          <FilterSection className={classes.filter} filterList={filterList} />
        </div>
        {/* categories section */}
        <div
          className={classNames({
            // "hidden": drain.isOpen,
            // If Filter panel is open then overflow-scroll sm:overflow-visible
            // "overflow-hidden": filterPanelOpen,
            'w-full h-full z-20 sm:relative sm:block pt-1 mb-4 xl:px-10 bg-white shadow-lg': true,
          })}
        >
          <div className={WallOfCardsStyles_rootContainerWall}>
            {/* Smart card section */}
            <div>
              <div className={classes.cards}>
                {noSearchWords ? <BrazeContentCards /> : void 0}
                {!noSearchWords && hasSwapCards && hasCards ? (
                  <div className={classes.smartContainer.root}>
                    <div className={classes.smartContainer.item}>
                      <h2 className={WallOfCardsStyles_title} ref={drainScrollRef}>
                        {`We found ${formatCardAmount(cardLists.filteredCards.length)} ${
                          cardLists.filteredCards.length > 1 ? 'gifts' : 'gift'
                        } for you`}
                      </h2>
                    </div>
                    <div className={classes.smartContainer.item}>
                      <SelectSorting
                        hidden={!getFilterTaxonomyOptions(filterList).sort.switched}
                        filterList={filterList}
                        className={classNames(['flex-1', 'relative'])}
                      />
                    </div>
                  </div>
                ) : !noSearchWords && hasSwapCards && !hasCards ? (
                  <>
                    <NoSearchResults
                      icon={noSearchResultsData.icon}
                      title={noSearchResultsData.title}
                      description={noSearchResultsData.description}
                    />
                    <h2 className={WallOfCardsStyles_title}>{noSearchResultsData.scp_title}</h2>
                    <h3 className={WallOfCardsStyles_description}>{noSearchResultsData.scp_description}</h3>
                    <div className={WallOfCardsStyles_cardsContainer}>
                      {allSwapCards.map((card: Card_t, index: number) => (
                        <Card key={card.uid} card={card} position={index + 1} skuDict={skuDict || EMPTY_ARRAY} />
                      ))}
                    </div>
                  </>
                ) : noSearchWords && hasSwapCards && hasCards ? (
                  <div>
                    {!drain.hasSort() && smartCardHeroSection}
                    <div className={classes.smartContainer.root}>
                      <div className={classes.smartContainer.item}>
                        {drain.sort === SortValue.Featured ? smartCardStoreContent : void 0}
                      </div>
                      <div className={classes.smartContainer.item}>
                        <SelectSorting
                          hidden={!getFilterTaxonomyOptions(filterList).sort.switched}
                          filterList={filterList}
                          className={classNames(['flex-1', 'relative'])}
                        />
                      </div>
                    </div>
                    {drain.sort === SortValue.Featured ? (
                      <div className={WallOfCardsStyles_cardsContainer}>
                        {allSwapCards.map((card: Card_t, index: number) => {
                          return (
                            <Card key={card.uid} card={card} position={index + 1} skuDict={skuDict || EMPTY_ARRAY} />
                          );
                        })}
                      </div>
                    ) : (
                      void 0
                    )}
                  </div>
                ) : (
                  <>
                    <NoSearchResults
                      icon={noSearchResultsData.icon}
                      title={noSearchResultsData.title}
                      description={noSearchResultsData.description}
                    />
                    <h2 className={WallOfCardsStyles_title}>{noSearchResultsData.scp_title}</h2>
                    <h3 className={WallOfCardsStyles_description}>{noSearchResultsData.scp_description}</h3>
                    <div className={WallOfCardsStyles_cardsContainer}>
                      {allSwapCards.map((card: Card_t, index: number) => (
                        <Card key={card.uid} card={card} position={index + 1} skuDict={skuDict || EMPTY_ARRAY} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Search & Filters */}
            {/* Second Section */}
            <div>
              {eq(search, EMPTY_STRING) && size(currentInventory) === size(cardLists.filteredCards)
                ? retailerContent
                : void 0}
              <div className={WallOfCardsStyles_cardsContainer}>
                {displayedCards.map((card: Card_t, index: number) => (
                  <Card key={card.uid} card={card} position={index + 1} skuDict={skuDict || EMPTY_ARRAY} />
                ))}
              </div>
              {drain.hasSearch() ? (
                <div className={WallOfCardsStyles_similarResultsContainer}>
                  <SimilarResults.make cardLists={cardLists} className="mt-4" />
                </div>
              ) : (
                EMPTY_ELEMENT
              )}
            </div>
            {endOfPageStoreContent}
            {noSearchWords ? void 0 : <BrazeContentCards />}
          </div>
        </div>
      </div>
    </>
  );
};

export default WallOfCards;
